import { create } from 'zustand';
import { http } from '../lib/http';
import { toast } from 'sonner';
import useSyncStore from './sync';
import { INITIAL_STATE, MobileSessionsState } from '@/core/state-shape';
import { measure } from '@/lib/performance';

type MobileActions = {
  patch: (p: Partial<MobileSessionsState>) => void;
  disconnectDevice: (deviceId: string) => Promise<void>;
  logout: () => Promise<void>;
};

export const useMobileStore = create<MobileSessionsState & MobileActions>((set) => ({
  ...INITIAL_STATE.mobileSessions,
  patch: (p) => set(p),
  disconnectDevice: measure(async (deviceId) => {
    const toastId = toast.loading(`Disconnecting device...`);
    try {
      await http<void>('/api/auth/logout-device', {
        method: 'POST',
        body: JSON.stringify({ deviceId }),
        sendProjectId: false,
      });
      await useSyncStore.getState().fetchSnapshot(); // Refresh state from server
      toast.success('Device disconnected.', { id: toastId });
    } catch (e: any) {
      console.error('Failed to disconnect device', e);
      toast.error('Failed to disconnect device.', { id: toastId, description: e.message });
    }
  }, 'mobile.disconnectDevice'),
  logout: measure(async () => {
    const toastId = toast.loading('Logging out...');
    try {
        // Optimistically log out on the client, but also tell the server
        // to invalidate the token for security. We don't wait for the server response.
        http<void>('/api/auth/logout', { method: 'POST', sendProjectId: false });

        localStorage.removeItem('mobile_auth_token');
        toast.success('You have been logged out.', { id: toastId });
        
        // This will trigger a reload and redirect via AuthGuard
        window.location.href = '/pages/onboarding/mobile-auth';

    } catch (e: any) {
        console.error('Logout failed', e);
        // Even if the API call fails, proceed with client-side logout
        localStorage.removeItem('mobile_auth_token');
        toast.error('Could not log out from server, but you have been logged out locally.', { id: toastId, description: e.message });
        window.location.href = '/pages/onboarding/mobile-auth';
    }
  }, 'mobile.logout')
}));